import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { projectFilters, projects } from '../../data/projects';
import { getService } from '../../data/services';
import { BeforeAfterSlider } from '../BeforeAfterSlider';
import { SectionHeading } from '../ui/SectionHeading';
import type { Project, ProjectCategory } from '../../types';
function ProjectCard({
  project,
  onOpen
}: {
  project: Project;
  onOpen: (project: Project) => void;
}) {
  const {
    t,
    b
  } = useLanguage();
  return <article className="flex h-full flex-col overflow-hidden rounded-lg border border-navy/10 bg-white" data-id="element-306" data-mp="1xchpnik48ogb">
      <BeforeAfterSlider before={project.before} after={project.after} beforeAlt={b(project.beforeAlt)} afterAlt={b(project.afterAlt)} className="aspect-[3/2] w-full" data-id="element-307" data-mp="qw5pxd1nayim" />
      <div className="flex flex-1 flex-col p-6" data-id="element-308" data-mp="26e03fl9o884w">
        <p className="text-xs font-bold uppercase tracking-wider text-brand-700" data-id="element-309" data-mp="26fbco0lvy83e">
          {b(project.label)} • {project.city}
        </p>
        <h3 className="mt-2 font-display text-lg font-bold leading-snug text-navy" data-id="element-310" data-mp="1wgvhnmjqmeom">
          {b(project.label)} — {project.city} ({project.year})
        </h3>
        <p className="mt-3 text-sm leading-relaxed text-ink-muted" data-id="element-311" data-mp="21hjit11okbwk">{b(project.description)}</p>
        <div className="mt-auto flex flex-wrap items-center gap-x-5 gap-y-2 pt-5" data-id="element-312" data-mp="1mp9aq2efn1ro">
          <button type="button" onClick={() => onOpen(project)} className="inline-flex items-center gap-1.5 text-sm font-bold text-brand-700 transition-colors duration-150 ease-smooth hover:text-brand-800" data-id="element-313" data-mp="1riu0561ymnf9">
            {t('cta.viewProject')}
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-314" data-mp="13szo2qpgt2w2" />
          </button>
          <Link to={`/services/${project.serviceSlug}`} className="text-sm font-semibold text-ink-muted underline decoration-navy/20 underline-offset-4 hover:text-navy" data-id="element-315" data-mp="18pq278hploeu">
            {b(getService(project.serviceSlug)?.title ?? project.label)}
          </Link>
        </div>
      </div>
    </article>;
}
export function ProjectGallery() {
  const {
    t,
    b
  } = useLanguage();
  const [filter, setFilter] = useState<ProjectCategory | 'all'>('all');
  const [active, setActive] = useState<Project | null>(null);
  const visible = useMemo(() => filter === 'all' ? projects : projects.filter(project => project.category === filter), [filter]);
  useEffect(() => {
    if (!active) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setActive(null);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [active]);
  return <section id="projects" className="scroll-mt-24 bg-white py-20 md:py-24" aria-labelledby="projects-title" data-id="element-316" data-mp="1tvy169zt73fk">
      <div className="mx-auto max-w-page px-4 sm:px-6 lg:px-8" data-id="element-317" data-mp="1yuhrsnpcl3e9">
        <SectionHeading id="projects-title" title={t('projects.title')} subtitle={t('projects.sub')} data-id="element-318" data-mp="1ysyd31jcb0el" />

        <div className="mt-10 flex flex-wrap justify-center gap-2" role="group" aria-label={t('projects.title')} data-id="element-319" data-mp="23saw1b73bnbw">
          {projectFilters.map(option => <button key={option.id} type="button" onClick={() => setFilter(option.id)} aria-pressed={filter === option.id} className={['rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-150 ease-smooth', filter === option.id ? 'bg-navy text-white' : 'border border-navy/15 bg-white text-ink-muted hover:border-brand-700 hover:text-brand-700'].join(' ')} data-id="element-320" data-mp="2dj5iafkc6mxc">
              {b(option.label)}
            </button>)}
        </div>

        <p className="mt-5 text-center text-sm text-ink-muted" data-id="element-321" data-mp="2nky6xmusa894">{t('projects.drag')}</p>

        {visible.length === 0 ? <p className="mt-12 text-center text-ink-muted" data-id="element-322" data-mp="2wnh3xqbw1qng">{t('projects.none')}</p> : <div className="mx-auto mt-8 grid max-w-5xl gap-8 md:grid-cols-2" data-id="element-323" data-mp="2slvwwlm1c6l6">
            {visible.map(project => <ProjectCard key={project.id} project={project} onOpen={setActive} data-id="element-324" data-mp="24vrtae8uk6u6" />)}
          </div>}
      </div>

      <AnimatePresence data-id="element-325" data-mp="1zyhtq5rquesm">
        {active && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.16,
        ease: [0.23, 1, 0.32, 1]
      }} className="fixed inset-0 z-[60] flex items-center justify-center bg-navy-deep/80 p-4" onClick={() => setActive(null)} role="dialog" aria-modal="true" aria-label={`${b(active.label)} — ${active.city}`} data-id="element-326" data-mp="2dold8oz8nued">
            <motion.div initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.97
        }} transition={{
          duration: 0.2,
          ease: [0.23, 1, 0.32, 1]
        }} onClick={event => event.stopPropagation()} className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white" data-id="element-327" data-mp="171gzt1384x52">
              <div className="flex items-start justify-between gap-4 border-b border-navy/10 p-5" data-id="element-328" data-mp="1prf9tmql10bc">
                <div data-id="element-329" data-mp="1uq0cdstb3w2y">
                  <p className="text-xs font-bold uppercase tracking-wider text-brand-700" data-id="element-330" data-mp="1fvc7afrtiznv">
                    {b(active.label)} • {active.city} • {active.year}
                  </p>
                  <h3 className="mt-1 font-display text-xl font-bold text-navy" data-id="element-331" data-mp="4fx1qnljmzh5">
                    {b(getService(active.serviceSlug)?.title ?? active.label)}
                  </h3>
                </div>
                <button type="button" onClick={() => setActive(null)} aria-label={t('projects.close')} className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-navy/15 text-navy hover:border-brand-700 hover:text-brand-700" data-id="element-332" data-mp="2c1p10blq6597">
                  <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-333" data-mp="1xbzjv6x8lqna" />
                </button>
              </div>
              <BeforeAfterSlider before={active.before} after={active.after} beforeAlt={b(active.beforeAlt)} afterAlt={b(active.afterAlt)} className="aspect-[3/2] w-full" data-id="element-334" data-mp="1sf94ymx6513g" />
              <div className="p-6" data-id="element-335" data-mp="1eeq4q1yy2zbu">
                <p className="text-base leading-relaxed text-ink-soft" data-id="element-336" data-mp="1efrcnxknz5pc">{b(active.description)}</p>
                <div className="mt-6 flex flex-wrap gap-3" data-id="element-337" data-mp="3dq9ak7oybrha">
                  <Link to={`/services/${active.serviceSlug}`} className="inline-flex items-center gap-2 rounded-md bg-brand-700 px-5 py-3 text-sm font-bold text-white transition-colors duration-200 ease-smooth hover:bg-brand-800" data-id="element-338" data-mp="38robiblfrduf">
                    {t('projects.service')}: {b(getService(active.serviceSlug)?.title ?? active.label)}
                  </Link>
                  <Link to="/#contact" onClick={() => setActive(null)} className="inline-flex items-center gap-2 rounded-md bg-cta px-5 py-3 text-sm font-bold text-white transition-colors duration-200 ease-smooth hover:bg-cta-dark" data-id="element-339" data-mp="3uz1vsmmr6jrp">
                    {t('cta.scheduleInspection')}
                  </Link>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}